import { useState } from "react";
import { useSession } from "@/services/auth";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { HeroInputField } from "@/components/hero-form";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import type { BusinessAccount } from "@/api.types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";

interface SelectedAddress {
  addressComponents: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode?: string;
    country: string;
  };
  location: {
    lat: number;
    lng: number;
  };
}

type BusinessAccountSchema = {
  companyName: string;
  taxNumber?: string;
  address: {
    line1: string;
    line2?: string;
    city: string;
    province: string;
    postalCode?: string;
    country: string;
    location: {
      lat: number;
      lng: number;
    };
  };
};

export default function BusinessAccountScreen() {
  const { t } = useTranslation("app");
  const { toast } = useToast();
  const { customer, token } = useSession();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const businessAccountSchema = z.object({
    companyName: z.string().min(1, t("validation.companyNameRequired")),
    taxNumber: z.string().optional(),
    address: z.object({
      line1: z.string().min(1, t("validation.addressLine1Required")),
      line2: z.string().optional(),
      city: z.string().min(1, t("validation.cityRequired")),
      province: z.string().min(1, t("validation.provinceRequired")),
      postalCode: z.string().optional(),
      country: z.string().min(1, t("validation.countryRequired")),
      location: z.object({
        lat: z.number(),
        lng: z.number(),
      }),
    }),
  });

  const form = useForm<BusinessAccountSchema>({
    resolver: zodResolver(businessAccountSchema),
    defaultValues: {
      companyName: customer?.businessAccount?.companyName ?? "",
      taxNumber: customer?.businessAccount?.taxNumber ?? "",
      address: customer?.businessAccount?.address ?? undefined,
    },
  });

  const createBusinessAccountMutation = useMutation({
    mutationFn: async (data: BusinessAccountSchema) => {
      const response = await axios.post<BusinessAccount>(
        `${import.meta.env.VITE_API_URL}/api/v2/business-account`,
        {
          ...data,
          customerId: customer?.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customer"] });
      setIsCreateDialogOpen(false);
      form.reset();
      toast({
        title: t("businessAccountScreen.toast.success"),
        description: t("businessAccountScreen.toast.created"),
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("businessAccountScreen.toast.error"),
        description: t("businessAccountScreen.toast.createFailed"),
      });
    },
  });

  const updateBusinessAccountMutation = useMutation({
    mutationFn: async (data: BusinessAccountSchema) => {
      const response = await axios.patch<BusinessAccount>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customer?.id}/business-accounts/${customer?.businessAccount?.id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customer"] });
      setIsCreateDialogOpen(false);
      form.reset();
      toast({
        title: t("businessAccountScreen.toast.success"),
        description: t("businessAccountScreen.toast.updated"),
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("businessAccountScreen.toast.error"),
        description: t("businessAccountScreen.toast.updateFailed"),
      });
    },
  });

  const handleSubmit = (values: BusinessAccountSchema) => {
    if (customer?.businessAccount) {
      updateBusinessAccountMutation.mutate(values);
    } else {
      createBusinessAccountMutation.mutate(values);
    }
  };

  const isPending =
    createBusinessAccountMutation.isPending ||
    updateBusinessAccountMutation.isPending;

  const businessAccount = customer?.businessAccount;
  const formattedAddress = businessAccount?.address
    ? [
        businessAccount.address.line1,
        businessAccount.address.line2,
        [
          businessAccount.address.city,
          businessAccount.address.province,
          businessAccount.address.postalCode,
        ]
          .filter(Boolean)
          .join(", "),
        businessAccount.address.country,
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <div className="container max-w-xl space-y-8">
      <div>
        <h2 className="text-2xl font-semibold">
          {t("businessAccountScreen.title")}
        </h2>
        <p className="text-sm text-default-500">
          {businessAccount
            ? t("businessAccountScreen.subtitleUpdate")
            : t("businessAccountScreen.subtitleSetup")}
        </p>
      </div>

      {!businessAccount ? (
        <Card
          shadow="none"
          className="bg-default-50 border-2 border-dashed border-default-200"
        >
          <CardBody className="pt-6">
            <div className="text-center py-6">
              <p className="text-lg font-medium">
                {t("businessAccountScreen.emptyTitle")}
              </p>
              <p className="text-sm text-default-500 mt-1">
                {t("businessAccountScreen.emptyBody")}
              </p>
              <Button
                color="primary"
                className="mt-4"
                onPress={() => setIsCreateDialogOpen(true)}
              >
                {t("businessAccountScreen.createBusiness")}
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-4">
          <Input
            label={t("settings.companyName")}
            value={businessAccount.companyName ?? ""}
            isDisabled
            variant="flat"
          />
          <Input
            label={t("settings.taxNumber")}
            value={businessAccount.taxNumber ?? ""}
            isDisabled
            variant="flat"
          />
          <Input
            label={t("businessAccountScreen.address")}
            value={formattedAddress}
            isDisabled
            variant="flat"
          />
          <Button
            color="primary"
            fullWidth
            onPress={() => setIsCreateDialogOpen(true)}
          >
            {t("businessAccountScreen.editBusiness")}
          </Button>
        </div>
      )}

      <Modal
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        placement="center"
        backdrop="blur"
        disableAnimation
        size="2xl"
      >
        <ModalContent>
          {(onClose) => (
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <ModalHeader>
                {businessAccount
                  ? t("businessAccountScreen.updateTitle")
                  : t("businessAccountScreen.createTitle")}
              </ModalHeader>
              <ModalBody className="gap-4">
                <HeroInputField
                  control={form.control}
                  name="companyName"
                  label={t("settings.companyName")}
                  variant="flat"
                />
                <HeroInputField
                  control={form.control}
                  name="taxNumber"
                  label={t("businessAccountScreen.taxNumberOptional")}
                  variant="flat"
                />
                <div className="space-y-1.5">
                  <label className="block text-tiny font-medium text-default-700">
                    {t("businessAccountScreen.address")}
                  </label>
                  <AutocompleteAddress
                    onSelectedAddress={(
                      selectedAddress: SelectedAddress | null
                    ) => {
                      if (selectedAddress) {
                        form.setValue("address", {
                          line1: selectedAddress.addressComponents.line1,
                          line2: selectedAddress.addressComponents.line2,
                          city: selectedAddress.addressComponents.city,
                          province: selectedAddress.addressComponents.state,
                          postalCode:
                            selectedAddress.addressComponents.postalCode ?? "",
                          country: selectedAddress.addressComponents.country,
                          location: selectedAddress.location,
                        });
                      }
                    }}
                    placeholder={t("businessAccountScreen.searchPlaceholder")}
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" type="button" onPress={onClose}>
                  {t("actions.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  isDisabled={isPending}
                  isLoading={isPending}
                >
                  {isPending
                    ? t("actions.processing")
                    : businessAccount
                      ? t("actions.update")
                      : t("actions.create")}
                </Button>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
